import React, { useState, useEffect } from 'react';
import { TTSButton } from './TTSButton';
import { playSound } from '../audio';
import { BerberRugBorder } from './Sprites';
import { TaleSentenceAudioRecorder } from './TaleSentenceAudioRecorder';
import { setActiveScrollSession, clearActiveScrollSession } from '../utils/activeActivityManager';

interface ScrollDualScreenReaderProps {
  scroll: {
    title: string;
    content: string;
  };
  onClose: () => void;
  sanctuaryName?: string;
  isAdmin?: boolean;
}

interface TaleSentencePair {
  french: string;
  kabyle: string;
}

/**
 * Extracts each alternating pair: 1 French sentence + Kabyle translation.
 * Groups them efficiently into pages (2 to 3 pairs per page) to optimize space
 * and reduce total page count while keeping a large, readable font size.
 */
function buildTalePages(content: string): TaleSentencePair[][] {
  const rawPairs: TaleSentencePair[] = [];
  
  // Matches French sentence followed by (Kabyle translation)
  const regex = /([^()\n]+)\s*(\([^)]+\))/g;
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    const french = match[1].trim();
    const rawKabyle = match[2].trim();
    // Strip enclosing parentheses
    const kabyle = rawKabyle.replace(/^\s*\(|\)\s*$/g, '').trim();
    if (french && kabyle) {
      rawPairs.push({ french, kabyle });
    }
  }

  // Fallback if format is different
  if (rawPairs.length === 0) {
    const rawParagraphs = content.split('\n\n').filter(p => p.trim().length > 0);
    return rawParagraphs.map(p => [{ french: p.trim(), kabyle: '' }]);
  }

  const pages: TaleSentencePair[][] = [];
  let currentGroup: TaleSentencePair[] = [];
  let currentChars = 0;

  for (const pair of rawPairs) {
    const pairLength = pair.french.length + pair.kabyle.length;
    // Group up to 3 pairs or ~480 characters per page to optimize space and minimize page count
    if (currentGroup.length > 0 && (currentChars + pairLength <= 480) && currentGroup.length < 3) {
      currentGroup.push(pair);
      currentChars += pairLength;
    } else {
      if (currentGroup.length > 0) {
        pages.push(currentGroup);
      }
      currentGroup = [pair];
      currentChars = pairLength;
    }
  }

  if (currentGroup.length > 0) {
    pages.push(currentGroup);
  }

  return pages.length > 0 ? pages : [[{ french: content, kabyle: '' }]];
}

export const ScrollDualScreenReader: React.FC<ScrollDualScreenReaderProps> = ({
  scroll,
  onClose,
  sanctuaryName,
  isAdmin = false
}) => {
  const scrollKey = 'tamazight_active_scroll_' + scroll.title;
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(scrollKey);
      if (saved) {
        const p = parseInt(saved, 10);
        return isNaN(p) ? 0 : p;
      }
    } catch {}
    return 0;
  });
  const [, setRefreshAudioTrigger] = useState(0);

  const pages = buildTalePages(scroll.content);
  const totalPages = pages.length;

  // Save current active scroll session
  useEffect(() => {
    try {
      setActiveScrollSession(scroll.title, currentPage, sanctuaryName);
    } catch {}
  }, [scroll.title, currentPage, sanctuaryName]);

  const currentPairs = pages[currentPage] || [];
  const cleanTitleFr = scroll.title.replace(/\s*\([^)]*\)/g, '').trim();
  const cleanTitleKabMatch = scroll.title.match(/\(([^)]+)\)/);
  const cleanTitleKab = cleanTitleKabMatch ? cleanTitleKabMatch[1].replace(/^\s*\(|\)\s*$/g, '').trim() : '';

  const handlePrevPage = () => {
    if (currentPage > 0) {
      playSound('click');
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      playSound('click');
      setCurrentPage(prev => prev + 1);
    } else {
      playSound('click');
      try {
        clearActiveScrollSession(scroll.title);
      } catch {}
      onClose();
    }
  };

  const handleClose = () => {
    playSound('click');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-[#2a1d12]/90 backdrop-blur-sm flex justify-center items-center overflow-hidden select-none">
      <div className="w-full max-w-[440px] h-full bg-[#fbf7ee] flex flex-col font-mono text-stone-900 overflow-hidden relative shadow-2xl border-x-2 border-[#b45309]/30">
        
        {/* ========================================================= */}
        {/* 1. ÉCRAN DU HAUT : LE LIVRE DU CONTE                      */}
        {/* ========================================================= */}
        <div className="relative w-full flex-1 min-h-0 bg-[#fffdf8] text-amber-950 overflow-hidden flex flex-col justify-between shadow-[0_4px_12px_rgba(180,83,9,0.1)] z-20 select-text">
          {/* Authentic Woven Berber Runner at the very top */}
          <BerberRugBorder />

          {/* Top Decorative Header */}
          <div className="w-full bg-[#fefaf0] border-b border-amber-800/20 px-3 py-1.5 flex items-center justify-between gap-2 shrink-0 relative z-10">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm shrink-0">📜</span>
              <span className="text-[10px] sm:text-xs text-amber-950 font-bold uppercase tracking-widest truncate">
                {sanctuaryName ? `Sanctuaire de ${sanctuaryName}` : 'Conte Traditionnel Kabyle'}
              </span>
            </div>
            
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="bg-[#b45309] text-[#fffdf8] px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold tracking-wider shrink-0 shadow-xs whitespace-nowrap">
                Page {currentPage + 1} / {totalPages}
              </div>
            </div>
          </div>

          {/* Tale Title Banner */}
          <div className="px-3.5 py-2 bg-amber-100/70 border-b border-amber-800/20 shrink-0 flex items-center justify-between gap-2 relative z-10">
            <h2 className="font-serif text-[11px] sm:text-xs md:text-[13px] text-amber-950 uppercase tracking-tight leading-snug break-words flex-1 min-w-0 text-left">
              <span className="font-black text-stone-900">{cleanTitleFr}</span>
              {cleanTitleKab && (
                <span className="font-bold text-amber-900 ml-1.5">
                  • {cleanTitleKab}
                </span>
              )}
            </h2>
          </div>

          {/* Reading Body & Admin Studio */}
          <div 
            key={currentPage} 
            className="flex-1 w-full flex flex-col justify-start px-3.5 sm:px-5 py-3 sm:py-3.5 overflow-y-auto select-text break-words animate-in fade-in duration-150 z-10 space-y-3 sm:space-y-3.5"
          >
            {currentPairs.map((pair, idx) => (
              <div key={idx} className="space-y-2 bg-amber-50/70 p-3 sm:p-3.5 rounded-2xl border border-amber-900/15 shadow-xs">
                {/* 1 Phrase en français */}
                <p className="font-serif text-[16.5px] sm:text-[18.5px] md:text-[20px] leading-relaxed sm:leading-[1.7] text-stone-900 first-letter:text-2xl sm:first-letter:text-3xl first-letter:font-black first-letter:text-amber-800 first-letter:mr-1">
                  {pair.french}
                </p>
                {/* Traduction en Kabyle sans parenthèses */}
                {pair.kabyle && (
                  <div className="flex items-start justify-between gap-2.5 pt-2 border-t border-amber-800/15">
                    <p className="font-serif font-black text-[16px] sm:text-[18px] md:text-[19px] leading-relaxed sm:leading-[1.7] text-amber-900 tracking-wide flex-1">
                      {pair.kabyle}
                    </p>
                    <TTSButton text={pair.kabyle} className="text-amber-900 bg-amber-100/90 hover:bg-amber-200 border border-amber-800/25 shrink-0 mt-0.5 shadow-xs scale-105" />
                  </div>
                )}

                {/* Bouton & Module d'enregistrement sous chaque bloc de phrases (Mode Admin) */}
                {isAdmin && pair.kabyle && (
                  <div className="pt-2 border-t border-amber-800/15">
                    <TaleSentenceAudioRecorder
                      sentenceKabyle={pair.kabyle}
                      sentenceFrench={pair.french}
                      index={idx}
                      onAudioUpdated={() => setRefreshAudioTrigger(t => t + 1)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ========================================================= */}
        {/* 2. ÉCRAN DU BAS : COMMANDES DE NAVIGATION & FERMER       */}
        {/* ========================================================= */}
        <div className="relative w-full shrink-0 bg-[#fbf7ee] berber-weave-pattern flex flex-col p-3.5 sm:p-5 gap-3 z-10 border-t-2 border-amber-800/25 shadow-inner">
          
          {/* Main Action Buttons: Précédente / Suivante */}
          <div className="grid grid-cols-2 gap-3 h-16 sm:h-20">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className={`h-full rounded-2xl font-black text-xs sm:text-sm uppercase tracking-wider border-2 transition-all flex flex-col items-center justify-center gap-1 p-2 ${
                currentPage === 0
                  ? 'bg-[#f4efe4]/80 border-[#e2d8c3] text-stone-400 opacity-40 cursor-not-allowed'
                  : 'bg-[#fffdfa] hover:bg-[#fef3c7] text-stone-800 border-[#d97706]/50 shadow-md active:translate-y-0.5 active:shadow-none cursor-pointer'
              }`}
            >
              <svg className="w-5 h-5 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="17,4 7,12 17,20" />
              </svg>
              <span className="whitespace-nowrap font-cinzel">Page Précédente</span>
            </button>

            <button
              type="button"
              onClick={handleNextPage}
              className={`h-full rounded-2xl font-black text-xs sm:text-sm uppercase tracking-wider border-2 transition-all flex flex-col items-center justify-center gap-1 p-2 cursor-pointer active:translate-y-0.5 active:shadow-none shadow-md ${
                currentPage === totalPages - 1
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white border-emerald-400'
                  : 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-amber-500 text-stone-950 border-amber-500'
              }`}
            >
              {currentPage === totalPages - 1 ? (
                <span className="text-lg sm:text-xl font-black leading-none">✓</span>
              ) : (
                <svg className="w-5 h-5 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="7,4 17,12 7,20" />
                </svg>
              )}
              <span className="whitespace-nowrap font-cinzel">
                {currentPage === totalPages - 1 ? 'Terminer le Conte' : 'Page Suivante'}
              </span>
            </button>
          </div>

          {/* Close Button at the bottom */}
          <button
            type="button"
            onClick={handleClose}
            className="w-full py-3.5 sm:py-4 bg-[#fff1f2] hover:bg-[#ffe4e6] text-red-900 font-black text-xs sm:text-sm uppercase tracking-widest border-2 border-red-300 rounded-2xl shadow-md active:translate-y-0.5 active:shadow-none transition-all cursor-pointer flex items-center justify-center gap-2 shrink-0 font-cinzel"
          >
            <span className="text-sm">✖</span>
            <span>Fermer la lecture</span>
          </button>
        </div>

      </div>
    </div>
  );
};
